import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerUrl, soccerPath } from '../utils/paths';
import { canFormTeamsFromRsvps } from '../utils/formationConfig';
import { canFormOrEditTeams } from '../utils/practiceSchedule';
import SoccerFieldVisualization from './SoccerFieldVisualization';
import './PracticeTeamFormation.css';

const PracticeTeamFormation = () => {
  const { id: practiceId } = useParams();
  const navigate = useNavigate();
  const { user, isClubAdmin } = useAuth();
  const [practice, setPractice] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [numberOfTeams, setNumberOfTeams] = useState(2);
  const [teams, setTeams] = useState(null);
  const [canManagePracticeTeams, setCanManagePracticeTeams] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formationShareToken, setFormationShareToken] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [clubName, setClubName] = useState('');
  const [skillUpdatingPlayerId, setSkillUpdatingPlayerId] = useState(null);

  const loadPractice = useCallback(async () => {
    try {
      const data = await api.getPracticeById(practiceId, user?.id);
      setPractice(data);
      if (data.formationSettings?.numberOfTeams) {
        setNumberOfTeams(data.formationSettings.numberOfTeams);
      }
      if (data.formationShareToken) {
        setFormationShareToken(data.formationShareToken);
      }
      if (data.teamFormationId) {
        loadTeams(data.teamFormationId, user?.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load practice');
    }
  }, [practiceId, user?.id]);

  const loadAvailability = useCallback(async () => {
    try {
      const data = await api.getPracticeAvailability(practiceId, user?.id);
      const eligible = data.filter(
        (entry) => entry.status === 'AVAILABLE' || entry.status === 'RESERVE'
      );
      setAvailablePlayers(eligible);
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
  }, [practiceId, user?.id]);

  useEffect(() => {
    loadPractice();
    loadAvailability();
  }, [loadPractice, loadAvailability]);

  const loadTeams = async (teamId, viewerUserId) => {
    try {
      const data = await api.getTeamById(teamId, viewerUserId);
      setTeams(data);
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  };

  const handlePlayerToggle = (playerId) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlayers.length === availablePlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(availablePlayers.map(a => a.playerId));
    }
  };

  const getPlayingPlayerCount = (playerIds) =>
    playerIds.filter((playerId) => {
      const entry = availablePlayers.find((item) => item.playerId === playerId);
      return !entry || entry.status !== 'RESERVE';
    }).length;

  const handleFormTeams = async () => {
    const playingCount = getPlayingPlayerCount(selectedPlayers);
    if (playingCount < numberOfTeams) {
      setError(`Need at least ${numberOfTeams} available (non-reserve) players to form ${numberOfTeams} teams`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const teamResponse = await api.formPracticeTeams(
        practiceId,
        user.id,
        selectedPlayers,
        numberOfTeams
      );
      setTeams(teamResponse);
      setSuccess('Teams formed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to form teams');
    } finally {
      setLoading(false);
    }
  };

  const handleFormTeamsFromRsvps = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const teamResponse = await api.formTeamsFromRsvps(practiceId, user.id);
      setTeams(teamResponse);
      await loadPractice();
      setSuccess('Teams formed from RSVP votes!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to form teams from RSVPs');
    } finally {
      setLoading(false);
    }
  };

  const checkClubAdminStatus = useCallback(async () => {
    if (!practice || !user) return;
    
    try {
      const club = await api.getClubById(practice.clubId);
      setClubName(club?.name || '');
      const isClubOwner = club.adminUserId === user.id;
      const members = await api.getClubMembers(practice.clubId, null, user.id);
      const myMember = members.find(
        (m) =>
          m.player?.userId === user.id ||
          (m.player && m.player.id && m.player.email === user.email)
      );
      const isPracticeCoordinator = myMember?.roles?.includes('PRACTICE_COORDINATOR');
      setCanManagePracticeTeams(isClubOwner || isClubAdmin() || isPracticeCoordinator);
    } catch (err) {
      console.error('Failed to check practice team permissions:', err);
    }
  }, [practice, user, isClubAdmin]);

  useEffect(() => {
    if (practice && user) {
      checkClubAdminStatus();
    }
  }, [practice, user, checkClubAdminStatus]);

  useEffect(() => {
    const ensureShareToken = async () => {
      if (!practice?.teamFormationId || !user?.id || formationShareToken) return;
      try {
        const share = await api.ensurePracticeFormationShare(practiceId, user.id);
        if (share?.formationShareToken) {
          setFormationShareToken(share.formationShareToken);
        }
      } catch (err) {
        console.warn('Could not ensure formation share token:', err);
      }
    };
    ensureShareToken();
  }, [practice?.teamFormationId, user?.id, formationShareToken, practiceId]);

  const handleReshuffle = async () => {
    if (!teams || !window.confirm('Are you sure you want to reshuffle the teams? This will redistribute all players.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updatedTeams = await api.reshuffleTeams(teams.id, user.id, false);
      setTeams(updatedTeams);
      setSuccess('Teams reshuffled successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reshuffle teams');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeamsWithAvailability = async () => {
    if (!teams || !practice) return;
    const numTeams = teams.divisions?.length || numberOfTeams;
    try {
      const availability = await api.getPracticeAvailability(practiceId, user?.id);
      const eligible = availability.filter(
        (entry) => entry.status === 'AVAILABLE' || entry.status === 'RESERVE'
      );
      const eligiblePlayerIds = eligible.map((entry) => entry.playerId);
      const playingCount = eligible.filter((entry) => entry.status === 'AVAILABLE').length;
      if (playingCount < numTeams) {
        setError(`Need at least ${numTeams} available players to form ${numTeams} teams. Currently ${playingCount} available (${eligible.length - playingCount} reserve).`);
        return;
      }
      if (!window.confirm(`Update teams with current availability? This will re-form teams using ${playingCount} available and ${eligible.length - playingCount} reserve players into ${numTeams} teams.`)) {
        return;
      }
      setLoading(true);
      setError('');
      setSuccess('');
      const teamResponse = await api.formPracticeTeams(
        practiceId,
        user.id,
        eligiblePlayerIds,
        numTeams
      );
      setTeams(teamResponse);
      setAvailablePlayers(eligible);
      setSelectedPlayers(eligiblePlayerIds);
      setSuccess('Teams updated with current availability!');
      setTimeout(() => setSuccess(''), 3000);
      loadPractice();
    } catch (err) {
      setError(err.message || 'Failed to update teams');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayerToTeam = async (playerId) => {
    if (!teams) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const updatedTeams = await api.addPlayersToTeam(teams.id, [playerId], user.id, false);
      setTeams(updatedTeams);
      setSuccess('Player added to teams!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to add player');
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayerFromTeam = async (playerId, playerName) => {
    if (!teams) return;
    if (!window.confirm(`Remove ${playerName || 'this player'} from the teams?`)) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const updatedTeams = await api.removePlayerFromTeam(teams.id, playerId, user.id, false);
      setTeams(updatedTeams);
      setSuccess('Player removed from teams!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to remove player');
    } finally {
      setLoading(false);
    }
  };

  const handleSkillLevelSave = async (playerId, skillLevel) => {
    if (!teams) return;
    setSkillUpdatingPlayerId(playerId);
    setError('');
    try {
      const updatedTeams = await api.updateTeamPlayerSkill(teams.id, playerId, skillLevel, user.id, false);
      setTeams(updatedTeams);
    } catch (err) {
      setError(err.message || 'Failed to update player skill');
    } finally {
      setSkillUpdatingPlayerId(null);
    }
  };

  const handleMovePlayer = async (playerId, fromDivisionIndex, toDivisionIndex) => {
    if (!teams) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updatedTeams = await api.movePlayerBetweenTeams(
        teams.id,
        playerId,
        fromDivisionIndex,
        toDivisionIndex,
        user.id,
        false
      );
      setTeams(updatedTeams);
      setSuccess('Player moved successfully!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.message || 'Failed to move player');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to properly encode message for WhatsApp with emoji support
  const encodeWhatsAppMessage = (message) => {
    return encodeURIComponent(message);
  };

  // Get WhatsApp URL - use api.whatsapp.com for better emoji support
  const getWhatsAppUrl = (message) => {
    const encodedMessage = encodeWhatsAppMessage(message);
    return `https://api.whatsapp.com/send?text=${encodedMessage}`;
  };

  const shareViaWhatsApp = () => {
    if (!teams) return;

    // Define position order for sorting
    const positionOrder = {
      'GOALKEEPER': 1,
      'DEFENDER': 2,
      'MIDFIELDER': 3,
      'WINGER': 4,
      'FORWARD': 5,
      'FLEXIBLE': 6
    };

    // Function to group players by position, then sort alphabetically within each group
    const groupPlayersByPosition = (players) => {
      const grouped = {};
      
      // Group players by position
      players.forEach((player) => {
        const position = player.position || 'FLEXIBLE';
        if (!grouped[position]) {
          grouped[position] = [];
        }
        grouped[position].push(player);
      });
      
      // Sort players within each position group alphabetically
      Object.keys(grouped).forEach((position) => {
        grouped[position].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      
      return grouped;
    };

    let message = `⚽ *${teams.name}*\n\n`;
    if (teams.description) {
      message += `${teams.description}\n\n`;
    }
    // Practice date, time and location (so shared team has context)
    if (practice) {
      const practiceDate = practice.practiceDate ? new Date(practice.practiceDate) : null;
      if (practiceDate) {
        message += `📅 ${practiceDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })} at ${practiceDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n`;
      }
      if (practice.location) {
        message += `📍 *Location:* ${practice.location}\n`;
      }
      if (practice.title) {
        message += `📋 ${practice.title}\n`;
      }
      message += `\n`;
    }
    message += `👥 Total Players: ${teams.members.length}\n`;
    if (teams.reservePlayers?.length) {
      message += `🪑 Reserve: ${teams.reservePlayers.length}\n`;
    }
    message += `🏆 Teams: ${teams.numberOfTeams}\n\n`;

    if (teams.divisions && teams.divisions.length > 0) {
      message += `*Team Divisions:*\n\n`;
      teams.divisions.forEach((division) => {
        // Use emoji and color indicators for teams
        let teamHeader = division.teamName;
        if (division.teamName.includes('Red Team') || division.teamName.includes('🔴')) {
          teamHeader = `🔴 *Red Team*`;
        } else if (division.teamName.includes('Blue Team') || division.teamName.includes('🔵')) {
          teamHeader = `🔵 *Blue Team*`;
        } else {
          teamHeader = `*${division.teamName}*`;
        }
        message += `${teamHeader}\n`;
        if (division.formation) {
          message += `Formation: ${division.formation}\n`;
        }
        if (division.players && division.players.length > 0) {
          // Group players by position and sort alphabetically within each group
          const groupedPlayers = groupPlayersByPosition(division.players);
          
          // Display players grouped by position
          Object.keys(positionOrder).forEach((positionKey) => {
            if (groupedPlayers[positionKey] && groupedPlayers[positionKey].length > 0) {
              const positionName = positionKey.charAt(0) + positionKey.slice(1).toLowerCase().replace('_', ' ');
              message += `*${positionName}s:*\n`;
              groupedPlayers[positionKey].forEach((player) => {
                message += `• ${player.name} - ${positionName}\n`;
              });
              message += `\n`;
            }
          });
        } else {
          message += `\n`;
        }
      });
    }

    if (teams.reservePlayers && teams.reservePlayers.length > 0) {
      message += `*Reserve Players:*\n`;
      teams.reservePlayers.forEach((player) => {
        message += `• ${player.name} (Reserve)\n`;
      });
      message += `\n`;
    }

    // Add practice video links if available
    const videoLinks = practice && Array.isArray(practice.videoLinks) ? practice.videoLinks : [];
    if (videoLinks.length > 0) {
      message += `🎥 *Practice Videos:*\n`;
      videoLinks.forEach((link) => {
        message += `${link}\n`;
      });
      message += `\n`;
    }

    // Use api.whatsapp.com for better emoji support
    const whatsappUrl = getWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  // Share formation on the field via WhatsApp (formation-focused layout)
  const shareFormationViaWhatsApp = () => {
    if (!teams || !practice) return;

    const positionOrder = ['GOALKEEPER', 'DEFENDER', 'MIDFIELDER', 'WINGER', 'FORWARD', 'FLEXIBLE'];
    const groupPlayersByPosition = (players) => {
      const grouped = {};
      players.forEach((player) => {
        const position = player.position || 'FLEXIBLE';
        if (!grouped[position]) grouped[position] = [];
        grouped[position].push(player);
      });
      Object.keys(grouped).forEach((pos) => grouped[pos].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      return grouped;
    };

    const practiceDate = practice.practiceDate ? new Date(practice.practiceDate) : null;
    let message = `⚽ *Formation on the field*\n\n`;
    message += `*${practice.title || teams.name}*\n`;
    if (practiceDate) {
      message += `📅 ${practiceDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${practiceDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}\n`;
    }
    if (practice.location) {
      message += `📍 ${practice.location}\n`;
    }
    message += `\n`;

    if (teams.divisions && teams.divisions.length > 0) {
      teams.divisions.forEach((division) => {
        const isRed = division.teamName.includes('Red') || division.teamName.includes('🔴');
        const isBlue = division.teamName.includes('Blue') || division.teamName.includes('🔵');
        const header = isRed ? '🔴' : isBlue ? '🔵' : '⚪';
        message += `${header} *${division.teamName}*`;
        if (division.formation) message += ` (${division.formation})`;
        message += `\n`;

        if (division.players && division.players.length > 0) {
          const grouped = groupPlayersByPosition(division.players);
          positionOrder.forEach((posKey) => {
            if (grouped[posKey] && grouped[posKey].length > 0) {
              const posName = posKey.charAt(0) + posKey.slice(1).toLowerCase().replace('_', ' ');
              message += `  _${posName}s:_ `;
              message += grouped[posKey].map((p) => p.name).join(', ');
              message += `\n`;
            }
          });
        }
        message += `\n`;
      });
    }

    const whatsappUrl = getWhatsAppUrl(message);
    window.open(whatsappUrl, '_blank');
  };

  const getFormationShareUrl = () => {
    if (!formationShareToken) return '';
    return soccerUrl(`/public/practice-formation/${formationShareToken}`);
  };

  const handlePublishTeams = async (published) => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const updated = await api.setTeamsPublishedToPublic(practiceId, user.id, published);
      setPractice(updated);
      setSuccess(
        published
          ? 'Teams are now visible on the public practice page.'
          : 'Teams are hidden from the public page while you make changes.'
      );
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      setError(err.message || 'Failed to update public team visibility');
    } finally {
      setLoading(false);
    }
  };

  const handleShareFormationLinkViaWhatsApp = async () => {
    if (!practice || !user) return;

    let token = formationShareToken;
    if (!token) {
      try {
        const share = await api.ensurePracticeFormationShare(practiceId, user.id);
        token = share?.formationShareToken;
        if (token) setFormationShareToken(token);
      } catch (err) {
        setError(err.message || 'Failed to create share link');
        return;
      }
    }

    if (!token) {
      setError('Could not create share link');
      return;
    }

    const url = api.sharePracticeFormationViaWhatsApp({
      title: practice.title,
      practiceDate: practice.practiceDate,
      location: practice.location,
      shareToken: token,
      clubName: clubName || undefined,
      teamsFormed: !!practice.teamsPublishedToPublic,
    });
    window.open(url, '_blank');
  };

  const handleCopyFormationLink = async () => {
    if (!user) return;
    let token = formationShareToken;
    if (!token) {
      try {
        const share = await api.ensurePracticeFormationShare(practiceId, user.id);
        token = share?.formationShareToken;
        if (token) setFormationShareToken(token);
      } catch (err) {
        setError(err.message || 'Failed to create share link');
        return;
      }
    }
    const link = soccerUrl(`/public/practice-formation/${token}`);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      window.prompt('Copy this link:', link);
    }
  };

  if (!practice) {
    return <div className="loading">Loading practice...</div>;
  }

  if (teams) {
    const canEditTeams = canManagePracticeTeams && canFormOrEditTeams(practice);
    const teamMemberIds = new Set((teams.members || []).map((member) => member.id));
    const addablePlayers = availablePlayers.filter(
      (availability) => !teamMemberIds.has(availability.playerId)
    );

    return (
      <div className="practice-team-formation">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
          >
            ← Back
          </button>
          <h2 style={{ flex: '1', minWidth: '200px' }}>Teams Formed for {practice.title}</h2>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!canEditTeams && canManagePracticeTeams && (
          <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
            Team editing is locked because the game has started. You can still view the lineup and share links.
          </div>
        )}
        
        <div className="team-view">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <h3 style={{ flex: '1', minWidth: '200px' }}>{teams.name}</h3>
            {canEditTeams && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={handleUpdateTeamsWithAvailability}
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ 
                    whiteSpace: 'nowrap',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                  title="Re-form teams using the latest availability (add newly available players, remove unavailable)"
                >
                  {loading ? 'Please wait...' : '👥 Update with current availability'}
                </button>
                <button
                  onClick={handleReshuffle}
                  className="btn btn-secondary"
                  disabled={loading}
                  style={{ 
                    whiteSpace: 'nowrap',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem'
                  }}
                >
                  {loading ? 'Please wait...' : '🔄 Reshuffle'}
                </button>
              </div>
            )}
          </div>
          {teams.description && <p>{teams.description}</p>}
          
          {teams.divisions && teams.divisions.length > 0 && (
            <div className="team-divisions" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
              {teams.divisions.map((division, idx) => {
                // Determine team color based on team name
                const isRedTeam = division.teamName.includes('Red Team') || division.teamName.includes('🔴');
                const isBlueTeam = division.teamName.includes('Blue Team') || division.teamName.includes('🔵');
                const teamColor = isRedTeam ? '#dc3545' : isBlueTeam ? '#007bff' : '#6c757d';
                const teamBgColor = isRedTeam ? '#fff5f5' : isBlueTeam ? '#f0f8ff' : '#f9f9f9';
                const teamBorderColor = isRedTeam ? '#dc3545' : isBlueTeam ? '#007bff' : '#ddd';
                
                return (
                <div key={idx} className="division-card" style={{ 
                  border: `2px solid ${teamBorderColor}`, 
                  borderRadius: '8px', 
                  padding: '1rem', 
                  backgroundColor: teamBgColor 
                }}>
                  <h4 style={{ color: teamColor, marginTop: 0 }}>
                    {division.teamName.includes('Red Team') && !division.teamName.includes('🔴') ? '🔴 ' : ''}
                    {division.teamName.includes('Blue Team') && !division.teamName.includes('🔵') ? '🔵 ' : ''}
                    {division.teamName.replace('🔴 ', '').replace('🔵 ', '')}
                  </h4>
                  {division.averageSkill && (
                    <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '0.5rem' }}>
                      Avg Skill: {division.averageSkill.toFixed(1)}
                    </p>
                  )}
                  <div className="division-players" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {division.players && division.players.map((player) => (
                      <div key={player.id} className="player-tag" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        padding: '0.5rem',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <strong>{player.name}</strong>
                          <br />
                          <small>
                            {player.position.charAt(0) + player.position.slice(1).toLowerCase().replace('_', ' ')}
                          </small>
                          {canEditTeams && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <label style={{ fontSize: '0.8rem', color: '#555', display: 'block' }}>
                                Skill: {player.skillLevel || 5}/10
                                {skillUpdatingPlayerId === player.id && ' (saving…)'}
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="10"
                                value={player.skillLevel || 5}
                                disabled={loading || skillUpdatingPlayerId === player.id}
                                onChange={(e) => {
                                  const nextSkill = parseInt(e.target.value, 10);
                                  setTeams((prev) => ({
                                    ...prev,
                                    divisions: prev.divisions.map((divisionItem, divisionIdx) =>
                                      divisionIdx === idx
                                        ? {
                                            ...divisionItem,
                                            players: divisionItem.players.map((divisionPlayer) =>
                                              divisionPlayer.id === player.id
                                                ? { ...divisionPlayer, skillLevel: nextSkill }
                                                : divisionPlayer
                                            ),
                                          }
                                        : divisionItem
                                    ),
                                    members: prev.members.map((member) =>
                                      member.id === player.id ? { ...member, skillLevel: nextSkill } : member
                                    ),
                                  }));
                                }}
                                onMouseUp={(e) => handleSkillLevelSave(player.id, parseInt(e.target.value, 10))}
                                onTouchEnd={(e) => handleSkillLevelSave(player.id, parseInt(e.target.value, 10))}
                                style={{ width: '100%' }}
                              />
                            </div>
                          )}
                          {!canEditTeams && player.skillLevel && (
                            <small> (Skill: {player.skillLevel})</small>
                          )}
                        </div>
                        {canEditTeams && (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '0.25rem',
                            minWidth: '120px'
                          }}>
                            <button
                              type="button"
                              onClick={() => handleRemovePlayerFromTeam(player.id, player.name)}
                              className="btn btn-sm"
                              disabled={loading}
                              style={{
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Remove
                            </button>
                            {teams.divisions.map((targetDivision, targetIdx) => {
                              if (targetIdx === idx) return null;
                              return (
                                <button
                                  key={targetIdx}
                                  onClick={() => handleMovePlayer(player.id, idx, targetIdx)}
                                  className="btn btn-sm"
                                  disabled={loading}
                                  style={{ 
                                    fontSize: '0.75rem', 
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: 'var(--color-brand)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '3px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'left'
                                  }}
                                  title={`Move to ${targetDivision.teamName}`}
                                >
                                  → {targetDivision.teamName}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {teams.reservePlayers && teams.reservePlayers.length > 0 && (
            <div
              className="practice-team-formation__reserve-panel"
              style={{
                marginTop: '1.5rem',
                padding: '1rem',
                borderRadius: '8px',
                border: '1px solid #b6d4fe',
                background: '#f0f6ff',
              }}
            >
              <h4 style={{ marginTop: 0, color: '#084298' }}>🪑 Reserve Players ({teams.reservePlayers.length})</h4>
              <p style={{ color: '#555', fontSize: '0.9rem', marginTop: 0 }}>
                These players are on standby and are not assigned to a playing team.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {teams.reservePlayers.map((player) => (
                  <div
                    key={player.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0.75rem',
                      background: '#fff',
                      border: '1px solid #b6d4fe',
                      borderRadius: '4px',
                    }}
                  >
                    <div>
                      <strong>{player.name}</strong>
                      <span
                        style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          background: '#cfe2ff',
                          color: '#084298',
                          padding: '0.1rem 0.45rem',
                          borderRadius: '999px',
                        }}
                      >
                        Reserve
                      </span>
                      <br />
                      <small>
                        {player.position?.charAt(0) + player.position?.slice(1).toLowerCase().replace('_', ' ')}
                        {player.skillLevel ? ` • Skill: ${player.skillLevel}` : ''}
                      </small>
                    </div>
                    {canEditTeams && (
                      <button
                        type="button"
                        onClick={() => handleRemovePlayerFromTeam(player.id, player.name)}
                        className="btn btn-sm"
                        disabled={loading}
                        style={{
                          fontSize: '0.75rem',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {canEditTeams && addablePlayers.length > 0 && (
            <div
              className="practice-team-formation__add-players"
              style={{
                marginTop: '1.5rem',
                padding: '1rem',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                background: '#f8f9fa',
              }}
            >
              <h4 style={{ marginTop: 0 }}>Add available players</h4>
              <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>
                These players voted available but are not on the teams yet.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {addablePlayers.map((availability) => (
                  <div
                    key={availability.playerId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <span>
                      {availability.player?.name || `Player ${availability.playerId?.substring(0, 8)}`}
                      {availability.player?.position && ` — ${availability.player.position.replace('_', ' ')}`}
                      {availability.status === 'RESERVE' && (
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            background: '#cfe2ff',
                            color: '#084298',
                            padding: '0.1rem 0.45rem',
                            borderRadius: '999px',
                          }}
                        >
                          Reserve
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={loading}
                      onClick={() => handleAddPlayerToTeam(availability.playerId)}
                    >
                      Add to teams
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {canManagePracticeTeams && teams && (
            <div
              className="practice-team-formation__publish-panel"
              style={{
                marginTop: '1.5rem',
                padding: '1rem 1.15rem',
                borderRadius: '10px',
                border: '1px solid #dee2e6',
                background: practice?.teamsPublishedToPublic ? '#d4edda' : '#fff8e6',
              }}
            >
              <strong>
                {practice?.teamsPublishedToPublic
                  ? 'Teams are live on the public page'
                  : 'Teams are draft — not visible publicly yet'}
              </strong>
              <p style={{ margin: '0.5rem 0 0.85rem', color: '#555', fontSize: '0.95rem' }}>
                {practice?.teamsPublishedToPublic
                  ? (canEditTeams
                    ? 'Players can see the lineup and field layout on the shared link. Unpublish to hide while you adjust formations.'
                    : 'The lineup is locked because the game has started.')
                  : 'Finalize your lineup and formations, then publish when ready. Availability voting stays visible either way.'}
              </p>
              {canEditTeams && (
                <button
                  type="button"
                  className={`btn ${practice?.teamsPublishedToPublic ? 'btn-secondary' : 'btn-success'}`}
                  onClick={() => handlePublishTeams(!practice?.teamsPublishedToPublic)}
                  disabled={loading}
                >
                  {practice?.teamsPublishedToPublic ? 'Unpublish teams from public page' : 'Publish teams to public page'}
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleShareFormationLinkViaWhatsApp}
              className="btn btn-whatsapp"
              style={{ fontWeight: 600 }}
            >
              <span>📱</span> Share formation link via WhatsApp
            </button>
            <button
              type="button"
              onClick={handleCopyFormationLink}
              className="btn btn-secondary"
            >
              {linkCopied ? 'Link copied!' : 'Copy formation link'}
            </button>
            {formationShareToken && (
              <a
                href={getFormationShareUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary"
                style={{ textDecoration: 'none' }}
              >
                Preview public page
              </a>
            )}
            <button
              type="button"
              onClick={() => navigate(soccerPath(`/practices/${practiceId}/lineup`))}
              className="btn btn-primary"
              style={{ fontWeight: 600 }}
            >
              View lineup page
            </button>
            <button onClick={shareViaWhatsApp} className="btn btn-whatsapp">
              <span>📱</span> Share roster via WhatsApp
            </button>
            <button onClick={shareFormationViaWhatsApp} className="btn btn-whatsapp">
              <span>📱</span> Share formation text via WhatsApp
            </button>
            <button
              type="button"
              onClick={() => navigate(soccerPath(`/practices/${practiceId}/scrimmage-stats`))}
              className="btn btn-secondary"
              style={{ fontWeight: 600 }}
            >
              📊 Live scrimmage stats
            </button>
          </div>

          {/* Field Visualization */}
          <SoccerFieldVisualization teams={teams} practice={practice} readOnly={!canEditTeams} />
        </div>
      </div>
    );
  }

  const canEditTeams = canManagePracticeTeams && canFormOrEditTeams(practice);

  return (
    <div className="practice-team-formation">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-secondary"
          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
        >
          ← Back
        </button>
        <h2 style={{ flex: '1', minWidth: '200px' }}>Form Teams for {practice.title}</h2>
      </div>
      <p><strong>Date:</strong> {new Date(practice.practiceDate).toLocaleString()}</p>
      
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!canFormOrEditTeams(practice) && (
        <div className="alert alert-info">
          Team formation is locked because the game has already started.
        </div>
      )}

      <div className="form-section">
        <div className="form-group">
          <label>Number of Teams *</label>
          <input
            type="number"
            min="2"
            max="10"
            value={numberOfTeams}
            onChange={(e) => setNumberOfTeams(parseInt(e.target.value))}
            required
          />
        </div>

        <div className="form-group">
          <label>
            Select Players ({selectedPlayers.length} selected
            {selectedPlayers.length > 0 && `, ${getPlayingPlayerCount(selectedPlayers)} available`}
            )
            <button
              type="button"
              onClick={handleSelectAll}
              className="btn btn-sm btn-secondary"
              style={{ marginLeft: '1rem' }}
            >
              {selectedPlayers.length === availablePlayers.length ? 'Deselect All' : 'Select All'}
            </button>
          </label>
          <div className="players-list">
            {availablePlayers.length === 0 ? (
              <p>No available or reserve players yet. Players need to vote their availability first.</p>
            ) : (
              availablePlayers.map((availability) => (
                <div key={availability.playerId} className="player-checkbox">
                  <input
                    type="checkbox"
                    id={`player-${availability.playerId}`}
                    checked={selectedPlayers.includes(availability.playerId)}
                    onChange={() => handlePlayerToggle(availability.playerId)}
                  />
                  <label htmlFor={`player-${availability.playerId}`}>
                    {availability.player?.name || `Player ${availability.playerId?.substring(0, 8)}...`}
                    {availability.player?.position && ` - ${availability.player.position.replace('_', ' ')}`}
                    {availability.status === 'RESERVE' && (
                      <span style={{
                        marginLeft: '0.5rem',
                        fontSize: '0.75rem',
                        background: '#cfe2ff',
                        color: '#084298',
                        padding: '0.1rem 0.45rem',
                        borderRadius: '999px',
                      }}
                      >
                        Reserve
                      </span>
                    )}
                    {!availability.player && <span style={{ color: '#999', fontSize: '0.9em' }}> (ID: {availability.playerId?.substring(0, 8)}...)</span>}
                  </label>
                </div>
              ))
            )}
          </div>
        </div>

        {canEditTeams && practice && canFormTeamsFromRsvps(practice) && !practice.teamFormationId && (
          <button
            type="button"
            onClick={handleFormTeamsFromRsvps}
            className="btn btn-success"
            disabled={loading}
            style={{ marginBottom: '0.75rem' }}
          >
            {loading
              ? 'Forming...'
              : `Form teams from RSVPs (${practice.formationSettings?.rsvpEligibleCount ?? 0})`}
          </button>
        )}

        <button
          onClick={handleFormTeams}
          className="btn btn-primary"
          disabled={loading || getPlayingPlayerCount(selectedPlayers) < numberOfTeams || !canFormOrEditTeams(practice)}
        >
          {loading ? 'Forming Teams...' : 'Form Teams'}
        </button>
      </div>
    </div>
  );
};

export default PracticeTeamFormation;

